<template>
  <div
    class="text-message-meters"
    :style="[style, getStyle]"
  >
    <div class="text-message-meters-wrapper">
      <div
        ref="firstLineMeters"
        class="first-line-meters"
      >
        <div class="shadow">
          {{ getFirstLineText }}
        </div>
        <div class="text">
          {{ getFirstLineText }}
        </div>
      </div>
      <div
        ref="secondLineMeters"
        class="second-line-meters"
      >
        <div class="shadow second">
          {{ getSecondLineText }}
        </div>
        <div class="text">
          {{ getSecondLineText }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">

/**
 * The basic modal of core minigames framework
 */
import { defineComponent } from 'vue'
import gsap from 'gsap'
import { WindowAspect } from '@powerplay/core-minigames-ui-ssm'
import { textMessageMetersState } from '@/stores'

export default defineComponent({
  name: 'TextMessageMeters',
  mixins: [WindowAspect],
  props: {
    /**
     * Sets the styles. To make the native style binding work.
     */
    style: {
      type: Object,
      required: false,
      default: () => ({})
    },
    duration: {
      type: Number,
      default: 0.2
    },
    delayStart: {
      type: Number,
      default: 0.1
    },
    delayShowText: {
      type: Number,
      default: 1
    },
    delayBetweenTwoTexts: {
      type: Number,
      default: 0.5
    },

  },
  data() {

    return {
      timelineTextFirstShow: null as gsap.core.Timeline | null,
    }

  },
  computed: {
    getStyle() {

      return {
        transform: `scale(${this.scaleCoef})`,
        'transform-origin': 'center center'
      }

    },
    getFirstLineText() {

      return textMessageMetersState().firstLineText !== '' ?
        this.$t(textMessageMetersState().firstLineText).toUpperCase() :
        ''

    },
    getSecondLineText() {

      return this.$t(textMessageMetersState().secondLineText)

    }
  },
  mounted() {

    this.setUpTweens()

  },
  beforeUnmount() {

    this.resetAllTimelines()

  },
  methods: {
    setUpTweens() {

      const scaleCoef = this.scaleCoef ? this.scaleCoef : 1
      const bodyWidth = document.body.clientWidth / scaleCoef

      this.resetAllTimelines()

      // prvy riadok textu
      const elementFirstLine = this.$refs.firstLineMeters as HTMLElement
      const elementFirstLineValueLeft = bodyWidth * -0.2
      const elementFirstLineValueLeftEnd = bodyWidth * 0.2
      elementFirstLine.style.left = `${elementFirstLineValueLeft}px`
      const elementFirstLineValueTop = bodyWidth * 0.0029
      const elementFirstLineValueTopEnd = bodyWidth * -0.0029
      elementFirstLine.style.top = `${elementFirstLineValueTop}px`
      elementFirstLine.style.opacity = '0'

      // druhy riadok textu
      const elementSecondLine = this.$refs.secondLineMeters as HTMLElement
      const elementSecondLineValueRight = bodyWidth * -0.2
      const elementSecondLineValueRightEnd = bodyWidth * 0.2
      elementSecondLine.style.right = `${elementSecondLineValueRight}px`
      const elementSecondLineValueTop = bodyWidth * -0.0029
      const elementSecondLineValueTopEnd = bodyWidth * 0.0029
      elementSecondLine.style.top = `${elementSecondLineValueTop}px`
      elementSecondLine.style.opacity = '0'

      this.textFirstShow(
        this.delayStart,
        elementFirstLineValueLeftEnd,
        elementFirstLineValueTopEnd,
        elementSecondLineValueRightEnd,
        elementSecondLineValueTopEnd
      )

    },

    textFirstShow(
      delayStart: number,
      elementFirstLineValueLeftEnd: number,
      elementFirstLineValueTopEnd: number,
      elementSecondLineValueRightEnd: number,
      elementSecondLineValueTopEnd: number
    ) {

      this.timelineTextFirstShow = gsap.timeline()

        // zobrazenie prveho riadku
        .to(
          '.text-message-meters .first-line-meters',
          {
            left: '0px',
            top: '0px',
            ease: 'none',
            opacity: 1,
            duration: this.duration,
          },
          delayStart
        )

        // zobrazenie druheho riadku spolu s prvym riadkom
        .to(
          '.text-message-meters .second-line-meters',
          {
            right: '0px',
            top: '0px',
            ease: 'none',
            opacity: 1,
            duration: this.duration,
          },
          delayStart + this.duration
        )

        // schovanie prveho riadku
        .to(
          '.text-message-meters .first-line-meters',
          {
            duration: this.duration,
            opacity: 0,
            left: `${elementFirstLineValueLeftEnd}px`,
            top: `${elementFirstLineValueTopEnd}px`
          },
          delayStart + (this.duration * 2) + this.delayShowText
        )

        // schovanie druheho riadku spolu so schovanim prveho riadku
        .to(
          '.text-message-meters .second-line-meters',
          {
            duration: this.duration,
            opacity: 0,
            right: `${elementSecondLineValueRightEnd}px`,
            top: `${elementSecondLineValueTopEnd}px`,
            onComplete: () => {

              textMessageMetersState().showMessage = false

            }
          },
          delayStart + (this.duration * 2) + this.delayShowText
        )

    },

    resetAllTimelines() {

      this.timelineTextFirstShow?.kill()

    }
  }

})
/* eslint-disable */
</script>

<style lang="less">
.text-message-meters {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  font-family: Nunito Sans;
  width: 100%;
  height: 100%;
  position: absolute;
  
  .text {
    -webkit-background-clip: text;
    -moz-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent; 
    -moz-text-fill-color: transparent;
    position: relative;
  }
  
  .shadow {
    text-shadow: 1.2px 3.8px 1px #000;
    position: absolute;
    z-index: -1;
    
    &.second {
      text-shadow: 1.5px 2.6px 2px rgba(0, 0, 0, 0.96);
    }
    
  }
  
  .first-line-meters {
    font-size: 80px;
    font-weight: bold;
    position: relative;
    margin-top: -7px;
    line-height: 1.3;
    
    .text {
      stroke: 1px #2b2b2b;
      -webkit-text-stroke: 1px #2b2b2b;
      background-color: #ffe7be;
      background-image: linear-gradient(to top, #d19b3e 30%, #ffe7be 80%);
    }
    
  }
  
  .second-line-meters {
    font-size: 120px;
    font-weight: bold;
    position: relative;
    line-height: 1.3;
    margin-top: -13px;
    display: flex;
    justify-content: center;
    
    .text{
      stroke: 1px #000;
      -webkit-text-stroke: 1px #000;
      background-color: #fff;
      background-image: linear-gradient(to top, #a2a2a2 0%, #ffffff 55%);
    }
  }
}
</style>
